/**
 * Proposal Detail Page (Phase 23.2.2 — Living SOPs)
 *
 * Trainer-facing redline view for a single SOP proposal. 60/40 layout:
 *  - Left (60%): RedlineStep cards stacked by section → task → step
 *  - Right (40%): sticky sidebar with source, rationale, impact metrics,
 *                 safety panel, rejection history, queue nav, action stack
 *
 * Keyboard shortcuts (only fire when focus is OUTSIDE text inputs):
 *  - j  — next proposal (queue order)
 *  - k  — previous proposal
 *  - a  — approve (if enabled)
 *  - r  — reject (stub — modal lands in 23.2.4)
 *  - e  — revise with feedback (stub — lands in 23.2.5)
 *  - v  — master "viewed all" toggle
 *  - ?  — shortcut cheat sheet overlay
 *  - Esc — close cheat sheet
 *
 * Read-only mode triggers when the proposal's state != 'pending_review' —
 * checkboxes/actions hide, reviewer info surfaces at the top of the sidebar.
 */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  GitCompare,
  Keyboard,
  MessageSquarePlus,
  ShieldAlert,
  Sparkles,
  TrendingDown,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import {
  useSopProposal,
  useProposalQueueNav,
  useApproveProposal,
  useCascadeStatus,
  type ProposalDetailQueueContext,
  type SnapshotSection,
  type SnapshotStep,
  type SnapshotTask,
  type SafetyFlag,
  type SignedEvidenceItem,
  type SopProposalDetailResponse,
} from '../../hooks/useSopProposals';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { useRoleLevel } from '../../lib/hooks/useRoleLevel';
import { getScoreColor } from '../../lib/score-colors';
import { RedlineStep, type RedlineChangeType } from './components/RedlineStep';
import { ProgressOverlay } from './components/ProgressOverlay';
import RejectionModal from './components/RejectionModal';
import ReviseProposalMode from './components/ReviseProposalMode';
import type {
  ProposalInboxSort,
  SopDraftProposalSource,
  SopDraftProposalState,
  SopProposalOperation,
} from '@guild/shared';

// ── Source labels (reuse inbox colors) ──────────────────────────────────────

const SOURCE_LABEL: Record<SopDraftProposalSource, string> = {
  struggle_signal: 'Struggle Signal',
  performance_delta: 'Performance Delta',
  coach_mining: 'Coach Mining',
  manual: 'Manual (trainer)',
};

const STATE_LABEL: Record<SopDraftProposalState, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  published: 'Published',
  rejected: 'Rejected',
  superseded: 'Superseded',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface RedlineRow {
  key: string;
  sectionTitle: string;
  taskTitle: string;
  originalStep: SnapshotStep | null;
  proposedStep: SnapshotStep | null;
  changeType: RedlineChangeType;
  safetyFlag?: SafetyFlag;
  // Which IDs this row represents (used to match evidence).
  stepIds: string[];
}

// Walk the proposed snapshot to generate the ordered list of RedlineRows
// (one per modified step). Unchanged steps are NOT emitted — the redline view
// only surfaces proposals' deltas. Parent section/task titles come from the
// proposed snapshot (renamed sections show their new names in the breadcrumb).
function buildRedlineRows(
  target: SopProposalDetailResponse['targetVersionSnapshot'],
  proposed: SopProposalDetailResponse['proposedSnapshot'],
  operations: SopProposalOperation[],
  safetyFlags: SafetyFlag[]
): RedlineRow[] {
  const rows: RedlineRow[] = [];

  // Build a quick lookup for target steps by id.
  const targetStepById = new Map<string, SnapshotStep>();
  const targetSectionByStepId = new Map<string, { section: SnapshotSection; task: SnapshotTask }>();
  for (const s of target.sections) {
    for (const t of s.tasks) {
      for (const step of t.steps) {
        targetStepById.set(step.id, step);
        targetSectionByStepId.set(step.id, { section: s, task: t });
      }
    }
  }

  // Safety flag lookup by stepId and taskId for quick attach.
  const flagsByStepId = new Map<string, SafetyFlag>();
  const flagsByTaskId = new Map<string, SafetyFlag>();
  for (const f of safetyFlags) {
    if (f.stepId) flagsByStepId.set(f.stepId, f);
    if (f.taskId) flagsByTaskId.set(f.taskId, f);
  }

  // For each section in the proposed snapshot, emit rows for:
  //  - Each step with a _changeType marker.
  for (const section of proposed.sections) {
    for (const task of section.tasks) {
      for (const step of task.steps) {
        if (!step._changeType) continue;

        const originalId = step._originalStepId ?? step.id;
        const originalStep = targetStepById.get(originalId) ?? null;

        // Skip 'unchanged' rows — the redline only surfaces deltas.
        if (step._changeType === 'unchanged') continue;

        let changeType: RedlineChangeType = step._changeType;

        // Detect metadata-only (description identical but other fields moved)
        if (
          changeType === 'updated' &&
          originalStep &&
          originalStep.description === step.description
        ) {
          changeType = 'metadata-only';
        }

        // For deleted steps we want the BEFORE to render; the proposed side
        // shows the struck-through pre-delete text.
        const safetyFlag = flagsByStepId.get(originalId);

        rows.push({
          key: `step-${step._placeholderId ?? step.id}-${task.id}`,
          sectionTitle: section.title,
          taskTitle: task.title,
          originalStep,
          proposedStep: step,
          changeType,
          safetyFlag,
          stepIds: [originalId, step.id].filter(Boolean) as string[],
        });
      }
    }
  }

  // Check for task-level safety blocks (task_delete on a safety-critical task).
  for (const op of operations) {
    if (op.op === 'task_delete') {
      const flag = flagsByTaskId.get(op.taskId);
      if (flag && flag.severity === 'block') {
        rows.push({
          key: `task-delete-${op.taskId}`,
          sectionTitle: '(Section)',
          taskTitle: '(Task deleted)',
          originalStep: null,
          proposedStep: null,
          changeType: 'deleted',
          safetyFlag: flag,
          stepIds: [],
        });
      }
    }
  }

  return rows;
}

// ── Cheat sheet overlay ─────────────────────────────────────────────────────

const SHORTCUTS: Array<{ key: string; action: string }> = [
  { key: 'j', action: 'Next proposal in queue' },
  { key: 'k', action: 'Previous proposal in queue' },
  { key: 'a', action: 'Approve (if enabled)' },
  { key: 'r', action: 'Reject' },
  { key: 'e', action: 'Revise with feedback' },
  { key: 'v', action: 'Mark all steps viewed' },
  { key: '?', action: 'Show this cheat sheet' },
  { key: 'Esc', action: 'Close cheat sheet' },
];

function CheatSheetOverlay({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      dialogRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cheat-sheet-title"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Keyboard size={20} className="text-guild-green" />
            <h2
              id="cheat-sheet-title"
              className="text-lg font-bold text-gray-900"
            >
              Keyboard shortcuts
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>
        <ul className="mt-4 space-y-2">
          {SHORTCUTS.map((s) => (
            <li
              key={s.key}
              className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm"
            >
              <span className="text-gray-700">{s.action}</span>
              <kbd className="rounded bg-gray-100 px-2 py-1 font-mono text-xs font-semibold text-gray-800">
                {s.key}
              </kbd>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-gray-500">
          Shortcuts are suppressed while typing in text fields.
        </p>
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export default function ProposalDetailPage() {
  const { id: proposalId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Resolve role level for action-stack gating.
  // Phase 19.S frontend migration (May 6, 2026) — role flows from
  // DB-canonical /api/me via useRoleLevel, not the JWT claim. Role hierarchy:
  // super_admin (6), admin (5), trainer (4), manager (3), operator (1).
  const { isTrainerOrHigher: canActOnProposal } = useRoleLevel();

  // Seed queue context from URL (?state=..&source=..&sort=..) so j/k preserves
  // the inbox filter the user was browsing.
  const queueContext: ProposalDetailQueueContext = useMemo(() => {
    const state =
      (searchParams.get('state') as SopDraftProposalState | null) ?? undefined;
    const source =
      (searchParams.get('source') as SopDraftProposalSource | null) ??
      undefined;
    const sort =
      (searchParams.get('sort') as ProposalInboxSort | null) ?? undefined;
    return { state, source, sort };
  }, [searchParams]);

  const { data, isLoading, error } = useSopProposal(proposalId, queueContext);

  useDocumentTitle(
    data?.proposal.sopTitle
      ? `Proposal: ${data.proposal.sopTitle}`
      : 'Proposal Detail'
  );

  const queueNav = useProposalQueueNav(data);

  // Per-step review state.
  const [viewedSteps, setViewedSteps] = useState<Set<string>>(new Set());
  const [approvedSteps, setApprovedSteps] = useState<Set<string>>(new Set());
  const [masterReviewed, setMasterReviewed] = useState(false);

  // Cheat sheet overlay visibility.
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);

  // Cascade progress overlay visibility. Flipped on after approve mutation
  // resolves with a successful Phase A commit; flipped off from inside the
  // overlay once the user clicks Close on the success or timeout state.
  const [cascadeOverlayOpen, setCascadeOverlayOpen] = useState(false);
  // Phase 23.2.4: Rejection modal visibility. Flipped on by the Reject
  // button / `r` shortcut; flipped off on close/cancel/success.
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  // Phase 23.2.5: inline revise panel visibility. Unlike the rejection modal
  // (centered overlay), this is an in-flow component rendered below the
  // redline list — so the proposal stays visible while the trainer types
  // feedback. Toggled by the Revise button / `e` shortcut.
  const [reviseOpen, setReviseOpen] = useState(false);
  // Approve mutation + cascade status polling.
  const approveMutation = useApproveProposal();
  // Error message surfaced under the Approve button (4xx details from the
  // server). Cleared each time the overlay opens or the mutation re-fires.
  const [approveError, setApproveError] = useState<string | null>(null);
  const { data: cascadeStatus } = useCascadeStatus(
    proposalId,
    cascadeOverlayOpen
  );

  // Compute redline rows from target + proposed.
  const operations: SopProposalOperation[] = useMemo(() => {
    const ops = data?.proposal.changes?.operations;
    return Array.isArray(ops) ? (ops as SopProposalOperation[]) : [];
  }, [data]);

  const redlineRows = useMemo(() => {
    if (!data) return [];
    return buildRedlineRows(
      data.targetVersionSnapshot,
      data.proposedSnapshot,
      operations,
      data.safetyFlags
    );
  }, [data, operations]);

  // Determine read-only mode.
  const isReadOnly = data?.proposal.state !== 'pending_review';

  // Safety gate: any 'block' severity disables Approve permanently.
  const hasBlockingSafety = useMemo(() => {
    if (!data) return false;
    return (
      data.safetyFlags.some((f) => f.severity === 'block') ||
      data.proposal.safetyCheckPassed === false
    );
  }, [data]);

  // Approve gate: master reviewed + no blocking safety + trainer+.
  const approveEnabled =
    canActOnProposal &&
    masterReviewed &&
    !hasBlockingSafety &&
    !isReadOnly;

  const totalRowCount = redlineRows.length;
  const viewedCount = viewedSteps.size;
  // Q1 fix (23.2.3): relax the gate for proposals with no per-step rows.
  // totalRowCount === 0 happens for section/task-only proposals (e.g.,
  // whenToDoTrigger-only change). No per-step gate needed — master-review
  // checkbox serves as the implicit ack.
  const allViewed = totalRowCount === 0 || viewedCount >= totalRowCount;

  // Evidence map: for 23.2.2 evidence isn't step-scoped (it's proposal-level),
  // so we attach ALL evidence to ALL rows. A per-step attribution lands in
  // 23.4.1 when the harvester populates stepId on the evidence item.
  const evidenceForRow = (_row: RedlineRow): SignedEvidenceItem[] => {
    return data?.evidence ?? [];
  };

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleViewedChange = useCallback((key: string, viewed: boolean) => {
    setViewedSteps((prev) => {
      const next = new Set(prev);
      if (viewed) next.add(key);
      else next.delete(key);
      return next;
    });
    if (!viewed) {
      // Un-viewing a step also un-approves it.
      setApprovedSteps((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const handleApprovedChange = useCallback((key: string, approved: boolean) => {
    setApprovedSteps((prev) => {
      const next = new Set(prev);
      if (approved) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const handleMarkAllViewed = useCallback(() => {
    const all = new Set(redlineRows.map((r) => r.key));
    setViewedSteps(all);
  }, [redlineRows]);

  const navigateToProposal = useCallback(
    (id: string | null) => {
      if (!id) return;
      // Preserve queue context via URL query so the next page uses the same
      // filter/sort when computing its own next/prev.
      const sp = new URLSearchParams();
      if (queueContext.state) sp.set('state', queueContext.state);
      if (queueContext.source) sp.set('source', queueContext.source);
      if (queueContext.sort) sp.set('sort', queueContext.sort);
      const qs = sp.toString();
      navigate(`/sops/proposals/${id}${qs ? `?${qs}` : ''}`);
    },
    [navigate, queueContext]
  );

  const handleApprove = useCallback(() => {
    if (!proposalId) return;
    setApproveError(null);
    approveMutation.mutate(
      { proposalId, stepApprovalsViewed: true },
      {
        onSuccess: () => {
          // Phase A committed; kick off the cascade progress overlay. The
          // overlay mounts useCascadeStatus which starts polling at 500ms.
          setCascadeOverlayOpen(true);
        },
        onError: (err) => {
          // Surface server-side validation errors (409 already-reviewed,
          // 400 safety-check failure, 403 four-eyes) in a concise banner
          // under the Approve button rather than an alert() interrupt.
          const msg =
            err instanceof Error
              ? err.message
              : 'Approve failed. Please try again.';
          setApproveError(msg);
        },
      }
    );
  }, [proposalId, approveMutation]);

  const handleReject = useCallback(() => {
    // Phase 23.2.4: open the rejection modal.
    if (isReadOnly || !canActOnProposal) return;
    setRejectionModalOpen(true);
  }, [isReadOnly, canActOnProposal]);

  const handleRejected = useCallback(() => {
    // Close modal and navigate back to the inbox. TanStack Query invalidation
    // inside useRejectProposal handles cache freshness for the list.
    setRejectionModalOpen(false);
    navigate('/sops/proposals');
  }, [navigate]);

  const handleRevise = useCallback(() => {
    // Phase 23.2.5: toggle the inline revise panel. The panel mounts below
    // the redline list and handles its own submit / cancel / close. The
    // `e` keyboard shortcut calls this too.
    if (isReadOnly || !canActOnProposal) return;
    setReviseOpen((open) => !open);
  }, [isReadOnly, canActOnProposal]);

  // Fires when a revise completes successfully with actual changes. The
  // detail query is invalidated by the mutation hook; we close the panel
  // so the trainer re-reviews the now-updated proposal inline.
  const handleRevised = useCallback(() => {
    setReviseOpen(false);
    // Reset master-review + per-step viewed state so the trainer has to
    // re-tick through the revised content before Approve is re-enabled.
    // Without this, a trainer who already marked everything "viewed" on the
    // prior cycle could Approve a revised proposal without inspecting it.
    setViewedSteps(new Set());
    setApprovedSteps(new Set());
    setMasterReviewed(false);
  }, []);

  // ── Keyboard shortcuts ────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Suppress shortcuts while focus is inside a text input, textarea, or
      // contentEditable element — otherwise `j`/`k` would hijack typing.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditing =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable === true;

      if (e.key === 'Escape') {
        if (cheatSheetOpen) {
          e.preventDefault();
          setCheatSheetOpen(false);
        }
        // Phase 23.2.4: the rejection modal handles its own Escape-to-close
        // via its internal keydown listener. Fall through here so the global
        // handler doesn't double-dispatch.
        return;
      }
      if (isEditing) return;

      // Suppress shortcuts while cheat sheet overlay is visible — prevents
      // silent j/k navigation or action firing under the modal.
      if (cheatSheetOpen) return;

      // Phase 23.2.4: same treatment for the rejection modal. Without this
      // gate, pressing `r` while the modal is open would re-call handleReject
      // (a no-op since the modal is already open, but clean to block) and
      // `a`/`e`/`v` would fire actions on a stale proposal state.
      if (rejectionModalOpen) return;

      // Phase 23.2.5: suppress shortcuts while the inline revise panel is
      // open. Typing feedback inside the textarea is already guarded by
      // isEditing above (TEXTAREA check), but a trainer clicking outside the
      // textarea while the panel is open shouldn't see j/k navigate away or
      // `a` approve — the revise is a focused modal-like interaction even
      // though it's rendered inline.
      if (reviseOpen) return;

      // Modifier-free keystrokes only (plain single-key shortcuts).
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case 'j':
          e.preventDefault();
          navigateToProposal(queueNav?.nextId ?? null);
          break;
        case 'k':
          e.preventDefault();
          navigateToProposal(queueNav?.prevId ?? null);
          break;
        case 'a':
          if (approveEnabled) {
            e.preventDefault();
            handleApprove();
          }
          break;
        case 'r':
          if (!isReadOnly && canActOnProposal) {
            e.preventDefault();
            handleReject();
          }
          break;
        case 'e':
          if (!isReadOnly && canActOnProposal) {
            e.preventDefault();
            handleRevise();
          }
          break;
        case 'v':
          if (!isReadOnly) {
            e.preventDefault();
            handleMarkAllViewed();
          }
          break;
        case '?':
          e.preventDefault();
          setCheatSheetOpen(true);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [
    cheatSheetOpen,
    rejectionModalOpen,
    reviseOpen,
    approveEnabled,
    isReadOnly,
    canActOnProposal,
    queueNav,
    navigateToProposal,
    handleApprove,
    handleReject,
    handleRevise,
    handleMarkAllViewed,
  ]);

  // ── Render ─────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-64 rounded bg-gray-100" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-48 rounded-2xl bg-gray-100" />
            ))}
          </div>
          <div className="lg:col-span-2">
            <div className="h-[500px] rounded-2xl bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50">
          <AlertCircle size={32} className="text-rose-500" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-gray-900">
          Proposal not found or no longer accessible
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          It may have been removed or your role does not have access.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sops/proposals')}
          className="mt-6 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft size={14} />
          Back to inbox
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { proposal, affectedOperatorCount, rejectionHistory } = data;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/sops/proposals')}
            className="flex-shrink-0 rounded-lg border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50"
            aria-label="Back to inbox"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {SOURCE_LABEL[proposal.source]}
            </p>
            <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-gray-900">
              {proposal.sopTitle ?? 'SOP unavailable'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Review proposed changes
              {proposal.targetVersionNumber && (
                <>
                  {' '}
                  against{' '}
                  <span className="font-semibold text-gray-900">
                    v{proposal.targetVersionNumber}
                  </span>
                </>
              )}
              .{' '}
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                {STATE_LABEL[proposal.state]}
              </span>
              {/* Phase 23.2.5: show revision badge once the trainer has
                  cycled at least once. The current proposal is the
                  (reviseCount + 1)-th revision (the initial draft is
                  revision #1; the first revise produces #2; etc.). */}
              {proposal.reviseCount > 0 && (
                <>
                  {' '}
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-200"
                    title={`AI has revised this proposal ${proposal.reviseCount} time${proposal.reviseCount === 1 ? '' : 's'} based on trainer feedback.`}
                  >
                    Revision #{proposal.reviseCount + 1}
                  </span>
                </>
              )}
              {/* Phase 23.4.3: proposal-level source-trigger pill. Hidden
                  when sourceTriggerId is null (manual proposals + legacy V1
                  proposals that pre-date schema population). The link
                  routes to /assessments/triggers/:id; the destination
                  enforces orgId server-side for multi-tenant safety. */}
              {proposal.sourceTriggerId && (
                <>
                  {' '}
                  <Link
                    to={`/assessments/triggers/${proposal.sourceTriggerId}`}
                    className="inline-flex items-center gap-1 rounded-full bg-guild-mint/40 px-2 py-0.5 text-xs font-semibold text-guild-green ring-1 ring-guild-green/20 hover:underline"
                    title={`AI authored from trigger ${proposal.sourceTriggerId}. Open the trigger detail.`}
                  >
                    Trigger {proposal.sourceTriggerId.slice(0, 8).toUpperCase()}
                    <ChevronRight size={10} />
                  </Link>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left column — redline stack */}
        <div className="space-y-4 lg:col-span-3">
          {redlineRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white p-12">
              <Sparkles size={32} className="text-gray-300" />
              <p className="mt-4 text-sm text-gray-500">
                No per-step changes to render. This proposal may contain only
                section/task-level edits.
              </p>
            </div>
          ) : (
            redlineRows.map((row) => (
              <RedlineStep
                key={row.key}
                proposalId={proposalId ?? ''}
                originalStep={row.originalStep}
                proposedStep={row.proposedStep}
                changeType={row.changeType}
                sectionTitle={row.sectionTitle}
                taskTitle={row.taskTitle}
                evidence={evidenceForRow(row)}
                safetyFlag={row.safetyFlag}
                viewed={viewedSteps.has(row.key)}
                approved={approvedSteps.has(row.key)}
                onViewedChange={(v) => handleViewedChange(row.key, v)}
                onApprovedChange={(v) => handleApprovedChange(row.key, v)}
                readOnly={isReadOnly}
                // Phase 23.4.2: Request Clip button is role-gated to trainer+
                // matching the sidebar action stack. Managers see frames but
                // not the upgrade button.
                canRequestClip={!isReadOnly && canActOnProposal}
              />
            ))
          )}

          {/* Phase 23.2.5: inline revise panel. Anchored below the redline
              stack so the trainer can keep the proposal visible while typing
              feedback. Only rendered when the trainer toggles it open AND the
              proposal is actionable (pending_review + trainer+ role). */}
          {proposalId && !isReadOnly && canActOnProposal && (
            <ReviseProposalMode
              proposalId={proposalId}
              isOpen={reviseOpen}
              onClose={() => setReviseOpen(false)}
              onRevised={handleRevised}
              reviseCount={proposal.reviseCount}
            />
          )}
        </div>

        {/* Right column — sticky sidebar */}
        <aside className="lg:col-span-2">
          <div className="sticky top-6 space-y-4">
            {/* Reviewer info (read-only states only) */}
            {isReadOnly && (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 text-sm">
                <p className="font-semibold text-gray-900">Already reviewed</p>
                <dl className="mt-3 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Reviewer</dt>
                    <dd className="font-medium text-gray-800">
                      {proposal.reviewerUserName ?? '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">State</dt>
                    <dd className="font-medium text-gray-800">
                      {STATE_LABEL[proposal.state]}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">At</dt>
                    <dd className="font-medium text-gray-800">
                      {formatDateTime(
                        proposal.approvedAt ??
                          proposal.rejectedAt ??
                          proposal.publishedAt
                      )}
                    </dd>
                  </div>
                </dl>
                {proposal.reviewNote && (
                  <p className="mt-3 rounded-md bg-white p-2 text-xs text-gray-700">
                    <span className="font-semibold">Note:</span>{' '}
                    {proposal.reviewNote}
                  </p>
                )}
              </div>
            )}

            {/* Rationale */}
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Why this change?
              </p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
                {proposal.rationale || '(No rationale provided.)'}
              </p>
              {proposal.authoredByUserName && (
                <p className="mt-3 text-xs text-gray-500">
                  Authored by{' '}
                  <span className="font-medium text-gray-700">
                    {proposal.authoredByUserName}
                  </span>
                </p>
              )}
            </div>

            {/* Impact metrics */}
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Impact
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div
                  className={`rounded-xl p-3 text-center ${getScoreColor(
                    proposal.impactScore,
                    'bg'
                  )}`}
                >
                  <p
                    className={`text-2xl font-bold tabular-nums ${getScoreColor(
                      proposal.impactScore,
                      'text'
                    )}`}
                  >
                    {Math.round(proposal.impactScore)}
                  </p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                    Score
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3 text-center">
                  <p className="text-2xl font-bold tabular-nums text-gray-900">
                    {Math.round((proposal.confidence ?? 0) * 100)}%
                  </p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                    Confidence
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3 text-center">
                  <p className="flex items-center justify-center gap-1 text-2xl font-bold tabular-nums text-gray-900">
                    <Users size={16} className="text-gray-500" />
                    {affectedOperatorCount}
                  </p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                    Operators
                  </p>
                </div>
              </div>
              {proposal.conflictDetected && (
                <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                  <GitCompare
                    size={12}
                    className="mt-0.5 flex-shrink-0"
                    strokeWidth={2.5}
                  />
                  <span>Cross-SOP conflict detected.</span>
                </div>
              )}
            </div>

            {/* Measured improvement (Phase 23.5.2 — performance-delta only).
                Surfaces the authoritative detector numbers that justify the
                proposal. Only rendered when `changes.measuredImprovement` is
                populated — struggle-signal / manual / coach-mining proposals
                leave this field undefined. */}
            {proposal.source === 'performance_delta' &&
              proposal.changes?.measuredImprovement && (
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    <TrendingDown size={14} strokeWidth={2.5} />
                    Measured improvement
                  </p>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div className="flex items-baseline justify-between">
                      <dt className="text-xs text-gray-600">Sample size</dt>
                      <dd className="font-semibold tabular-nums text-gray-900">
                        {proposal.changes.measuredImprovement.sampleSize}
                      </dd>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <dt className="text-xs text-gray-600">Written p50</dt>
                      <dd className="font-semibold tabular-nums text-gray-900">
                        {proposal.changes.measuredImprovement.oldP50Seconds.toFixed(
                          1
                        )}
                        s
                      </dd>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <dt className="text-xs text-gray-600">Observed p50</dt>
                      <dd className="font-semibold tabular-nums text-gray-900">
                        {proposal.changes.measuredImprovement.newP50Seconds.toFixed(
                          1
                        )}
                        s
                      </dd>
                    </div>
                    <div className="flex items-baseline justify-between border-t border-emerald-100 pt-2">
                      <dt className="text-xs font-medium text-emerald-700">
                        Speedup
                      </dt>
                      <dd className="text-base font-bold tabular-nums text-emerald-700">
                        {proposal.changes.measuredImprovement.deltaPercent >= 0
                          ? '+'
                          : ''}
                        {proposal.changes.measuredImprovement.deltaPercent.toFixed(
                          1
                        )}
                        %
                      </dd>
                    </div>
                    {(proposal.changes.measuredImprovement.ciLower !==
                      undefined ||
                      proposal.changes.measuredImprovement.ciUpper !==
                        undefined) && (
                      <div className="flex items-baseline justify-between">
                        <dt className="text-xs text-gray-600">CI bounds</dt>
                        <dd className="text-xs font-medium tabular-nums text-gray-800">
                          {proposal.changes.measuredImprovement.ciLower?.toFixed(
                            1
                          ) ?? '—'}
                          s –{' '}
                          {proposal.changes.measuredImprovement.ciUpper?.toFixed(
                            1
                          ) ?? '—'}
                          s
                        </dd>
                      </div>
                    )}
                  </dl>
                  <p className="mt-3 text-[10px] leading-relaxed text-emerald-800/80">
                    Detector-authoritative values. Operators are completing
                    this step faster than the written method while still
                    clearing compliance and safety gates.
                  </p>
                </div>
              )}

            {/* Safety panel */}
            {(data.safetyFlags.length > 0 || !proposal.safetyCheckPassed) && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-rose-700">
                  <ShieldAlert size={14} strokeWidth={2.5} />
                  Safety
                </p>
                {!proposal.safetyCheckPassed && (
                  <p className="mt-2 text-sm font-semibold text-rose-900">
                    Safety validator flagged this proposal.
                  </p>
                )}
                {proposal.safetyCheckNotes && (
                  <p className="mt-1 text-xs text-rose-800">
                    {proposal.safetyCheckNotes}
                  </p>
                )}
                {data.safetyFlags.length > 0 && (
                  <ul className="mt-3 space-y-2 text-xs">
                    {data.safetyFlags.map((f) => (
                      <li
                        key={`${f.operationIndex}-${f.flagKind}`}
                        className="flex items-start gap-2 rounded-md bg-white/70 p-2"
                      >
                        {f.severity === 'block' ? (
                          <ShieldAlert
                            size={12}
                            className="mt-0.5 flex-shrink-0 text-rose-600"
                            strokeWidth={2.5}
                          />
                        ) : (
                          <AlertTriangle
                            size={12}
                            className="mt-0.5 flex-shrink-0 text-amber-600"
                            strokeWidth={2.5}
                          />
                        )}
                        <span className="text-rose-900">{f.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Rejection history */}
            {rejectionHistory.length > 0 && (
              <details className="rounded-2xl border border-gray-100 bg-white p-4">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Rejection history on this SOP ({rejectionHistory.length})
                </summary>
                <ul className="mt-3 space-y-2 text-xs">
                  {rejectionHistory.slice(0, 10).map((r, i) => (
                    <li
                      key={`${r.stepId}-${r.source}-${i}`}
                      className="rounded-md bg-gray-50 p-2"
                    >
                      <p className="font-medium text-gray-800">
                        {r.sectionTitle ? (
                          <>
                            {r.sectionTitle}
                            {r.taskTitle && ` / ${r.taskTitle}`}
                          </>
                        ) : (
                          '(Step reference)'
                        )}
                      </p>
                      <p className="mt-1 text-gray-600">{r.stepDescription}</p>
                      <p className="mt-1 text-[10px] text-gray-500">
                        Reason:{' '}
                        <span className="font-medium text-gray-700">
                          {r.reason}
                        </span>{' '}
                        · {formatDateTime(r.createdAt)}
                        {r.expiresAt
                          ? ` · expires ${formatDateTime(r.expiresAt)}`
                          : ' · permanent'}
                      </p>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {/* Phase 23.2.5: revision history. Only rendered when the
                proposal has been revised at least once (trainerFeedbackHistory
                is null for unrevised proposals). Shows the chronological
                feedback chain so the trainer can see why the proposal
                evolved into its current shape. */}
            {proposal.trainerFeedbackHistory &&
              proposal.trainerFeedbackHistory.length > 0 && (
                <details className="rounded-2xl border border-indigo-100 bg-indigo-50/30 p-4">
                  <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    Revision history ({proposal.trainerFeedbackHistory.length})
                  </summary>
                  <ol className="mt-3 space-y-3 text-xs">
                    {proposal.trainerFeedbackHistory.map((h, i) => (
                      <li
                        key={`${h.revisedAt}-${i}`}
                        className="rounded-md bg-white p-3 ring-1 ring-indigo-100"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-indigo-900">
                            Cycle {i + 1}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            {formatDateTime(h.revisedAt)}
                          </span>
                        </div>
                        <p className="mt-1.5 whitespace-pre-wrap text-gray-800">
                          <span className="font-medium text-gray-600">
                            Feedback:
                          </span>{' '}
                          {h.feedback}
                        </p>
                      </li>
                    ))}
                  </ol>
                </details>
              )}

            {/* Queue nav */}
            {queueNav && queueNav.position && (
              <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-3">
                <button
                  type="button"
                  onClick={() => navigateToProposal(queueNav.prevId)}
                  disabled={!queueNav.prevId}
                  aria-keyshortcuts="k"
                  className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>
                <span className="text-xs font-medium text-gray-500">
                  {queueNav.position.index + 1} of {queueNav.position.total}
                </span>
                <button
                  type="button"
                  onClick={() => navigateToProposal(queueNav.nextId)}
                  disabled={!queueNav.nextId}
                  aria-keyshortcuts="j"
                  className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            )}

            {/* Action stack */}
            {!isReadOnly && canActOnProposal && (
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={masterReviewed}
                    disabled={!allViewed}
                    onChange={(e) => setMasterReviewed(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-guild-green focus:ring-guild-green/30 disabled:opacity-50"
                  />
                  <span className="text-gray-800">
                    I have reviewed all modified steps
                    {!allViewed && (
                      <span className="ml-1 text-xs text-gray-500">
                        ({viewedCount}/{totalRowCount} viewed)
                      </span>
                    )}
                  </span>
                </label>

                <div className="mt-4 space-y-2">
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={!approveEnabled}
                    aria-keyshortcuts="a"
                    title={
                      hasBlockingSafety
                        ? 'Safety-critical content is being deleted or weakened — Approve is blocked.'
                        : !masterReviewed
                          ? 'Mark all steps viewed first.'
                          : 'Approve proposal'
                    }
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-guild-green px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Check size={16} strokeWidth={2.5} />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    aria-keyshortcuts="r"
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-50"
                  >
                    <XCircle size={16} strokeWidth={2.5} />
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={handleRevise}
                    aria-keyshortcuts="e"
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <MessageSquarePlus size={16} strokeWidth={2.5} />
                    Revise with feedback
                  </button>
                </div>

                {hasBlockingSafety && (
                  <p className="mt-3 flex items-start gap-1.5 rounded-md bg-rose-50 p-2 text-xs text-rose-800">
                    <ShieldAlert
                      size={12}
                      className="mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <span>
                      Approve is disabled: this proposal deletes or weakens
                      safety-critical content.
                    </span>
                  </p>
                )}

                {/* Server-side approve error (409 already-reviewed,
                    400 safety-check, 403 four-eyes, etc.). */}
                {approveError && (
                  <p
                    role="alert"
                    className="mt-3 flex items-start gap-1.5 rounded-md bg-rose-50 p-2 text-xs text-rose-800"
                  >
                    <AlertCircle
                      size={12}
                      className="mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <span>{approveError}</span>
                  </p>
                )}
              </div>
            )}

            {/* Manager read-only ribbon (Phase 23.2.7).
                canActOnProposal is true for trainer (4)+. Managers (3) see
                this ribbon on any pending_review proposal — clarifies why
                the action stack is absent from the sidebar. */}
            {!isReadOnly && !canActOnProposal && (
              <div
                role="status"
                className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4"
              >
                <Eye
                  size={18}
                  className="mt-0.5 flex-shrink-0 text-blue-600"
                  strokeWidth={2}
                />
                <div>
                  <p className="text-sm font-semibold text-blue-900">
                    Read-only — viewing as manager
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-blue-800">
                    Approve, Reject, and Revise actions are restricted to
                    trainers and admins. You can review this proposal for
                    awareness, but cannot take action on it.
                  </p>
                </div>
              </div>
            )}

            {/* Shortcuts hint */}
            <button
              type="button"
              onClick={() => setCheatSheetOpen(true)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg text-xs text-gray-500 hover:text-gray-700"
            >
              <Keyboard size={12} />
              Press <kbd className="rounded bg-gray-100 px-1 py-0.5 font-mono">?</kbd> for keyboard shortcuts
            </button>
          </div>
        </aside>
      </div>

      <CheatSheetOverlay
        open={cheatSheetOpen}
        onClose={() => setCheatSheetOpen(false)}
      />

      {/* Phase 23.2.4: rejection modal. Only mounted when the trainer opens
          it — internal state is reset by the modal's own useEffect on
          isOpen so no cleanup is needed here. */}
      {proposalId && (
        <RejectionModal
          isOpen={rejectionModalOpen}
          proposalId={proposalId}
          onClose={() => setRejectionModalOpen(false)}
          onRejected={handleRejected}
        />
      )}

      {/* Cascade progress overlay — mounted once the approve mutation
          succeeds. ProgressOverlay polls cascade status via
          useCascadeStatus(proposalId, cascadeOverlayOpen). On 'complete' we
          navigate to the SOP detail page with ?v=<newVersionId> so the
          operator-facing "what changed" banner (Phase 23.2.7) picks it up. */}
      <ProgressOverlay
        isOpen={cascadeOverlayOpen}
        status={cascadeStatus}
        onComplete={({ versionId }) => {
          // Short delay so the trainer registers the success state before the
          // navigate flips the page. The overlay itself is showing "vX.YZ
          // published" + changeSummary during this window.
          window.setTimeout(() => {
            setCascadeOverlayOpen(false);
            if (proposal?.sopId) {
              navigate(`/sops/${proposal.sopId}?v=${versionId}`);
            }
          }, 1500);
        }}
        onTimeout={() => {
          // Client-side timeout — log for observability but keep the overlay
          // open in 'timeout' state until the user acknowledges. The cascade
          // may still finish in the background; the resume worker is the
          // durability backstop if the API process actually crashed.
          console.warn(
            '[ProposalDetail] cascade client-timeout reached; continuing to poll in background'
          );
        }}
        onClose={() => setCascadeOverlayOpen(false)}
      />
    </div>
  );
}
